/**
 * useInterviewSocket -- minimal, resilient WebSocket client for the AI
 * interview room.
 *
 * Protocol (envelope-based, additive):
 *   client -> server:
 *     { type: "auth",           resume_token, supabase_jwt }
 *     { type: "ping",           ts }
 *     { type: "candidate_text", text }        // placeholder until 1.10b
 *     { type: "candidate_event",action }      // paused, tab_switched, ...
 *     { type: "candidate_end" }
 *
 *   server -> client:
 *     { type: "ack",         session_id, persona_id, language, heartbeat_interval }
 *     { type: "ai_text",     role, text }
 *     { type: "session_complete", reason }
 *     { type: "error",       code, message? }
 *     { type: "pong",        ts }
 *
 * Resilience:
 *   - heartbeat every 5s (matches server)
 *   - exponential reconnect across ~2 min (1s, 2s, 4s, 8s, 15s, 30s, 60s)
 *     -- aggressive at first so a hotel-wifi blip is invisible, then
 *     spaced enough that a 1-2 min outage still recovers cleanly
 *   - keeps message log so UI can re-render after reconnect
 *
 * Phase 1.10a only ships text + state plumbing -- audio frames come in 1.10b
 * and just slot in as new envelope types.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type InterviewSocketStatus =
  | 'idle'
  | 'connecting'
  | 'authenticating'
  | 'open'
  | 'reconnecting'
  | 'closed'
  | 'error';

export interface InterviewTranscriptLine {
  ts_ms: number;
  speaker: 'ai' | 'candidate' | 'system';
  text: string;
}

interface AckFrame {
  type: 'ack';
  session_id: string;
  language: 'de' | 'en' | string;
  persona_id: string | null;
  started_at: number | null;
  heartbeat_interval: number;
}

interface UseInterviewSocketArgs {
  wsUrl: string;            // wss://api.cevee.ai/api/v1/interview/sessions/{id}/ws
  resumeToken: string | null;
  getJwt: () => Promise<string | null>;
  /**
   * Anonymous warm-intro (Lite-ATS): the candidate has no Cevee account, so
   * the WS authenticates on the resume token alone (no Supabase JWT). The
   * backend only honours this for sessions flagged anonymous at /start.
   */
  anonymous?: boolean;
  enabled: boolean;
  onSessionComplete?: (reason: string) => void;
  onError?: (code: string, message?: string) => void;
  /** Called for every base64-encoded PCM16 audio delta from the AI. */
  onAiAudio?: (b64: string) => void;
  /** AI speech start/stop -- used to drive the persona bubble. */
  onAiSpeechState?: (state: 'started' | 'stopped') => void;
  /** Candidate's own speech activity (server VAD on Azure side). */
  onCandidateSpeechState?: (state: 'started' | 'stopped') => void;
  /** Streaming STT deltas for the candidate's own words (live subtitle). */
  onCandidateTextDelta?: (text: string) => void;
  /** Final committed candidate line (clears the live subtitle bubble). */
  onCandidateText?: (text: string) => void;
  /** Streaming text deltas for assistant (subtitles). */
  onAiTextDelta?: (text: string) => void;
  /** Final committed assistant line (clears live subtitle bubble). */
  onAiText?: (text: string) => void;
  /**
   * Server detected that a "candidate turn" was actually a Whisper
   * hallucination on silence/noise (e.g. "Untertitel der Amara.org-
   * Community"). The partial AI bubble we already accumulated from
   * the interrupted opening should be discarded -- the AI will
   * immediately re-do its opening.
   */
  onAiTextDrop?: () => void;
  /**
   * Fired when the server confirms a successful resume (i.e. this WS is
   * a reconnect of an existing session and the AI has been re-seeded
   * with the prior transcript). Lets the UI show a "Wieder verbunden"
   * toast so the candidate knows their progress wasn't lost.
   */
  onResumed?: (transcriptLines: number) => void;
}

const HEARTBEAT_MS = 5_000;
// 7 attempts spanning ~2 min. Past this it's almost certainly a real
// outage (router rebooted, mobile network gone, laptop sleep). We
// surface `reconnect_giveup` so the UI can show a reload CTA.
const RECONNECT_DELAYS = [1_000, 2_000, 4_000, 8_000, 15_000, 30_000, 60_000];

// Hard floor between two socket opens, regardless of what triggered the
// connect (backoff slot, forceReconnect on tab-focus/resume/online, or a
// getJwt() null-retry). The api-gateway rate-limits WS connects per IP and
// 403s (close-before-accept -> browser "WebSocket connection failed") once
// the budget is blown, which turns a brief reconnect flurry into a permanent
// lockout. Capping our own connect rate here guarantees we stay well under
// that budget even if several reconnect triggers fire at once.
const MIN_CONNECT_INTERVAL_MS = 2_500;
// Logged-in WS auth: retry a few times before giving up. Supabase session
// hydration / refresh can briefly return null even for a signed-in user;
// closing immediately on the first null caused a close -> reconnect loop
// (browser 1000, ~2s cadence, UI flickering live/reconnecting).
const AUTH_JWT_RETRIES = 6;
const AUTH_JWT_RETRY_MS = 500;

export function useInterviewSocket({
  wsUrl,
  resumeToken,
  getJwt,
  anonymous = false,
  enabled,
  onSessionComplete,
  onError,
  onAiAudio,
  onAiSpeechState,
  onCandidateSpeechState,
  onCandidateTextDelta,
  onCandidateText,
  onAiTextDelta,
  onAiText,
  onAiTextDrop,
  onResumed,
}: UseInterviewSocketArgs) {
  const [status, setStatus] = useState<InterviewSocketStatus>('idle');
  const [transcript, setTranscript] = useState<InterviewTranscriptLine[]>([]);
  const [ack, setAck] = useState<AckFrame | null>(null);

  // Refs the side-effects use without re-triggering useEffect.
  const socketRef = useRef<WebSocket | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const manualCloseRef = useRef(false);
  // True only AFTER the server's `ack` (i.e. our auth frame was accepted).
  // The mic AudioWorklet starts producing frames the instant the socket is
  // OPEN, but on the logged-in path we `await getJwt()` before sending the
  // auth frame -- so without this gate an `audio` frame can reach the server
  // BEFORE `auth`, the server replies `auth_required` and closes, and we
  // reconnect into an endless loop. Nothing but the auth frame itself may be
  // sent until this is true.
  const authedRef = useRef(false);
  // Timestamp of the last socket-open, used to enforce MIN_CONNECT_INTERVAL_MS.
  const lastConnectAtRef = useRef(0);
  // We keep a ref to the latest `connect` callback so utility callbacks
  // (forceReconnect) can reach it without listing `connect` as a dep,
  // which would re-create them every render and break the listeners
  // that captured them.
  const connectRef = useRef<(() => Promise<void>) | null>(null);

  const clearTimers = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const sendRaw = useCallback((frame: object) => {
    const ws = socketRef.current;
    // Hard gate: never let any frame (audio / ping / text / event) precede
    // the auth handshake. The auth frame is sent via ws.send() directly in
    // onopen, so it is unaffected by this guard.
    if (!authedRef.current) {
      return false;
    }
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify(frame));
        return true;
      } catch (e) {
        console.warn('ws send failed', e);
      }
    }
    return false;
  }, []);

  // Force an immediate reconnect, bypassing the exponential backoff
  // schedule. Used when we have a strong external signal that the
  // connection is or just became dead -- e.g. the tab unfroze, the
  // user came back from a long background, the network just flapped
  // back to 'online'. Resets the attempt counter so the next "real"
  // failure starts with a 1s backoff again instead of carrying over
  // a stale 60s wait from before the freeze.
  //
  // Carefully a no-op in three cases so we never interrupt an active
  // conversation:
  //   1) manualCloseRef -- session already cleanly completed / errored
  //   2) WS is OPEN  -- the AI may be mid-sentence
  //   3) WS is CONNECTING -- an attempt is already in flight; doubling
  //      down would orphan the in-flight socket and we'd end up with
  //      two parallel WebSockets racing for ack
  const forceReconnect = useCallback(() => {
    if (manualCloseRef.current) return;
    const ws = socketRef.current;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    reconnectAttemptRef.current = 0;
    // Detach the old socket from the ref BEFORE calling close() so
    // the soon-to-fire onclose handler sees `socketRef.current !== ws`
    // and skips scheduling its own auto-retry (would otherwise race
    // with the connect() we're about to do here).
    if (ws && ws.readyState !== WebSocket.CLOSED) {
      socketRef.current = null;
      try { ws.close(); } catch { /* noop */ }
    }
    void connectRef.current?.();
  }, []);

  const sendText = useCallback(
    (text: string) => sendRaw({ type: 'candidate_text', text }),
    [sendRaw],
  );

  const sendEvent = useCallback(
    (action: string) => sendRaw({ type: 'candidate_event', action }),
    [sendRaw],
  );

  const sendAudio = useCallback(
    (b64: string) => sendRaw({ type: 'audio', data: b64 }),
    [sendRaw],
  );

  // Tells the server how many ms of AI audio are still queued for
  // playback after Azure has already sent response.done. The server
  // uses this to extend its echo guard so speaker -> mic bleed during
  // the tail of the response can't trigger Azure VAD and cancel the
  // un-played portion of the audio.
  const sendPlaybackBuffered = useCallback(
    (ms: number) => sendRaw({ type: 'ai_playback_buffered', ms }),
    [sendRaw],
  );

  const sendArtifact = useCallback(
    (kind: 'code' | 'canvas' | 'numbers' | 'notepad', payload: Record<string, unknown>) =>
      sendRaw({ type: 'candidate_artifact', kind, payload }),
    [sendRaw],
  );

  const sendArtifactEvent = useCallback(
    (kind: 'code' | 'canvas' | 'numbers' | 'notepad', action: string, details?: Record<string, unknown>) =>
      sendRaw({ type: 'candidate_artifact_event', kind, action, details: details || {} }),
    [sendRaw],
  );

  const endSession = useCallback(() => {
    sendRaw({ type: 'candidate_end' });
  }, [sendRaw]);

  const connect = useCallback(async () => {
    if (!enabled || !wsUrl || !resumeToken) return;
    const existing = socketRef.current;
    if (existing) {
      const rs = existing.readyState;
      if (rs === WebSocket.OPEN || rs === WebSocket.CONNECTING) return;
      // Old socket still shutting down -- wait for it instead of opening a
      // second parallel WebSocket (both would auth, one gets orphaned).
      if (rs === WebSocket.CLOSING) {
        setStatus(reconnectAttemptRef.current === 0 ? 'connecting' : 'reconnecting');
        reconnectTimerRef.current = setTimeout(() => connect(), 200);
        return;
      }
    }

    clearTimers();

    // Connect-rate floor: never open two sockets closer than
    // MIN_CONNECT_INTERVAL_MS apart. If we're early, defer to the remainder
    // instead of hammering the gateway (which would 403 us into a lockout).
    const sinceLast = Date.now() - lastConnectAtRef.current;
    if (sinceLast < MIN_CONNECT_INTERVAL_MS) {
      setStatus(reconnectAttemptRef.current === 0 ? 'connecting' : 'reconnecting');
      reconnectTimerRef.current = setTimeout(() => {
        connect();
      }, MIN_CONNECT_INTERVAL_MS - sinceLast);
      return;
    }

    setStatus(reconnectAttemptRef.current === 0 ? 'connecting' : 'reconnecting');
    lastConnectAtRef.current = Date.now();
    authedRef.current = false;  // gate frames until this socket is ack'd

    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl);
    } catch (e) {
      setStatus('error');
      onError?.('socket_init_failed', e instanceof Error ? e.message : undefined);
      return;
    }
    socketRef.current = ws;

    ws.onopen = () => {
      setStatus('authenticating');
      // Anonymous warm-intro: resume-token-only auth, no JWT round-trip.
      if (anonymous) {
        ws.send(
          JSON.stringify({
            type: 'auth',
            resume_token: resumeToken,
          }),
        );
        return;
      }
      void (async () => {
        for (let attempt = 0; attempt < AUTH_JWT_RETRIES; attempt++) {
          if (ws.readyState !== WebSocket.OPEN) return;
          let jwt: string | null = null;
          try {
            jwt = await getJwt();
          } catch (e) {
            console.warn('ws: getJwt threw', e);
          }
          if (jwt) {
            ws.send(
              JSON.stringify({
                type: 'auth',
                resume_token: resumeToken,
                supabase_jwt: jwt,
              }),
            );
            return;
          }
          if (attempt < AUTH_JWT_RETRIES - 1) {
            await new Promise((r) => setTimeout(r, AUTH_JWT_RETRY_MS));
          }
        }
        // Gave up -- do NOT leave manualClose false or onclose will loop.
        manualCloseRef.current = true;
        onError?.('no_jwt');
        setStatus('error');
        try { ws.close(); } catch { /* noop */ }
      })();
    };

    ws.onmessage = (ev) => {
      let frame: any;
      try {
        frame = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
      } catch {
        return;
      }
      const t = frame?.type as string | undefined;

      if (t === 'ack') {
        reconnectAttemptRef.current = 0;
        authedRef.current = true;  // auth accepted -> frames may now flow
        setAck(frame as AckFrame);
        setStatus('open');

        // Start heartbeat
        const intervalMs = (frame.heartbeat_interval || 5) * 1000;
        if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = setInterval(() => {
          sendRaw({ type: 'ping', ts: Date.now() });
        }, Math.min(intervalMs, HEARTBEAT_MS));
        return;
      }

      if (t === 'pong') return;

      if (t === 'resumed') {
        const lines = Number(frame.transcript_lines) || 0;
        try { onResumed?.(lines); } catch { /* noop */ }
        return;
      }

      if (t === 'ai_audio') {
        if (frame.data && onAiAudio) onAiAudio(String(frame.data));
        return;
      }

      if (t === 'ai_text_delta') {
        if (onAiTextDelta && frame.text) onAiTextDelta(String(frame.text));
        return;
      }

      if (t === 'ai_text_drop') {
        // Server abandoned an in-flight AI utterance (phantom barge-in).
        // Clear ONLY the live streaming bubble -- we never remove a
        // committed transcript line, because a line the AI actually said
        // (even a truncated fragment) is real content and must not vanish
        // from the chat or the later history.
        try { onAiTextDrop?.(); } catch { /* noop */ }
        return;
      }

      if (t === 'ai_speech') {
        if (onAiSpeechState && (frame.state === 'started' || frame.state === 'stopped')) {
          onAiSpeechState(frame.state);
        }
        return;
      }

      if (t === 'candidate_speech') {
        if (onCandidateSpeechState && (frame.state === 'started' || frame.state === 'stopped')) {
          onCandidateSpeechState(frame.state);
        }
        return;
      }

      if (t === 'candidate_transcript_delta') {
        if (onCandidateTextDelta && frame.text) onCandidateTextDelta(String(frame.text));
        return;
      }

      if (t === 'candidate_transcript') {
        const committed = String(frame.text || '');
        setTranscript((prev) =>
          prev.concat({
            ts_ms: Date.now(),
            speaker: 'candidate',
            text: committed,
          }),
        );
        // Committed line landed -> clear the live subtitle bubble.
        try { onCandidateText?.(committed); } catch { /* noop */ }
        return;
      }

      if (t === 'ai_text') {
        const committed = String(frame.text || '');
        const speaker = frame.role === 'system' ? 'system' : 'ai';
        setTranscript((prev) => {
          const next = committed.trim();
          // Mirror the backend's _append_transcript de-dupe: one spoken AI
          // turn can arrive as two overlapping ai_text frames (transcript.done
          // + a response.done partial flush, or a truncation-retry
          // continuation). Without this the live chat shows the same line
          // twice (a suffix bubble + the full line) even though the stored
          // transcript is clean.
          if (speaker === 'ai' && prev.length > 0 && next) {
            const last = prev[prev.length - 1];
            if (last.speaker === 'ai') {
              const prevText = (last.text || '').trim();
              if (prevText) {
                // New is a duplicate/subset of the last line -> drop it.
                if (next === prevText || prevText.includes(next)) return prev;
                // New supersedes the last line (extends it) -> replace.
                if (next.includes(prevText)) {
                  const merged = prev.slice(0, -1);
                  merged.push({ ts_ms: Date.now(), speaker, text: committed });
                  return merged;
                }
              }
            }
          }
          return prev.concat({ ts_ms: Date.now(), speaker, text: committed });
        });
        try { onAiText?.(committed); } catch { /* noop */ }
        return;
      }

      if (t === 'session_complete') {
        manualCloseRef.current = true;
        clearTimers();
        try { ws.close(); } catch { /* noop */ }
        onSessionComplete?.(String(frame.reason || 'unknown'));
        setStatus('closed');
        return;
      }

      if (t === 'error') {
        onError?.(String(frame.code || 'unknown'), frame.message);
        // Most errors from the server are fatal (invalid_jwt, wrong_candidate,
        // no_session_state) -- don't bother retrying.
        manualCloseRef.current = true;
        try { ws.close(); } catch { /* noop */ }
        setStatus('error');
        return;
      }
    };

    ws.onclose = () => {
      // If this socket was replaced by forceReconnect, do nothing --
      // the new socket is already in flight and we mustn't schedule a
      // competing auto-retry that would orphan it.
      if (socketRef.current !== ws) {
        return;
      }
      authedRef.current = false;  // this socket is gone; re-gate sends
      clearTimers();
      if (manualCloseRef.current) {
        setStatus('closed');
        return;
      }
      // Auto-reconnect with exponential backoff
      const attempt = reconnectAttemptRef.current;
      if (attempt >= RECONNECT_DELAYS.length) {
        setStatus('error');
        onError?.('reconnect_giveup');
        return;
      }
      const delay = RECONNECT_DELAYS[attempt];
      reconnectAttemptRef.current = attempt + 1;
      setStatus('reconnecting');
      reconnectTimerRef.current = setTimeout(() => {
        connect();
      }, delay);
    };

    ws.onerror = () => {
      // Browsers fire onerror right before onclose; let onclose handle retry.
    };
  }, [enabled, wsUrl, resumeToken, anonymous, clearTimers, sendRaw, getJwt, onSessionComplete, onError]);

  // Keep the latest connect() reachable from listener callbacks (see
  // forceReconnect, freeze/resume, visibility probe) without having to
  // pull `connect` into their dependency arrays.
  connectRef.current = connect;

  // Mount: connect
  useEffect(() => {
    manualCloseRef.current = false;
    reconnectAttemptRef.current = 0;
    if (enabled && resumeToken) {
      connect();
    }
    return () => {
      manualCloseRef.current = true;
      clearTimers();
      const ws = socketRef.current;
      if (ws && ws.readyState !== WebSocket.CLOSED) {
        try { ws.close(); } catch { /* noop */ }
      }
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, resumeToken, wsUrl]);

  // Page visibility -> signal pause/resume to server (audit-only) AND
  // proactively probe WS health when the tab comes back. A long
  // backgrounded / frozen tab often returns with a silently dead TCP
  // connection -- pings into it never fail until the server side times
  // out, which can take 30+ seconds. Probing readyState on `visible`
  // catches that immediately and forces a reconnect so the candidate
  // sees the "verbunden" badge within ~1s of tabbing back.
  //
  // forceReconnect() is itself a no-op when the WS is already OPEN or
  // mid-CONNECT, so we never interrupt an in-flight conversation.
  useEffect(() => {
    const onVis = () => {
      sendEvent(document.hidden ? 'tab_hidden' : 'tab_visible');
      if (!document.hidden) {
        forceReconnect();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [sendEvent, forceReconnect]);

  // Chrome Page Lifecycle API: a `freeze` event means the browser is
  // about to halt all JS in this tab (memory pressure / 5 min idle in
  // background). `resume` fires when the user comes back and JS is
  // allowed to run again. Without explicit handling the WS may stay in
  // a half-dead state on resume; we close it cleanly on freeze and
  // immediately reconnect on resume. The reconnect carries the
  // resume_token so the AI re-attaches with full transcript context.
  useEffect(() => {
    const onFreeze = () => {
      const ws = socketRef.current;
      if (ws && ws.readyState !== WebSocket.CLOSED) {
        socketRef.current = null;
        try { ws.close(); } catch { /* noop */ }
      }
    };
    const onResume = () => forceReconnect();
    document.addEventListener('freeze', onFreeze);
    document.addEventListener('resume', onResume);
    return () => {
      document.removeEventListener('freeze', onFreeze);
      document.removeEventListener('resume', onResume);
    };
  }, [forceReconnect]);

  // Network came back (laptop wifi handover, train tunnel exit, hotel
  // captive portal solved): try to reconnect immediately instead of
  // sitting through whatever backoff slot we were on when the network
  // dropped.
  useEffect(() => {
    const onOnline = () => forceReconnect();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [forceReconnect]);

  return {
    status,
    transcript,
    ack,
    sendText,
    sendEvent,
    sendAudio,
    sendPlaybackBuffered,
    sendArtifact,
    sendArtifactEvent,
    endSession,
    forceReconnect,
  };
}
